import { useCallback, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { qcReviewService } from "../../services/qcReview.service";
import type { QcSlide } from "../../types/qcReview";
import { QcSlideTableRow } from "./QcSlideTableRow";

interface Props {
  caseId: string;
  onClose: () => void;
}
export function QcSlideReviewModal({ caseId, onClose }: Props) {
  const client = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const query = useQuery({
    queryKey: ["qc-review", caseId],
    queryFn: () => qcReviewService.read(caseId),
    refetchInterval: 5000,
  });
  const invalidate = useCallback(async () => {
    await Promise.all(
      [
        "qc-review",
        "qc-review-queue",
        "pending-work",
        "pending-work-details",
        "pending-work-count",
        "pending-work-summary",
      ].map((key) => client.invalidateQueries({ queryKey: [key] }))
    );
  }, [client]);
  const handleSlideChange = useCallback(
    async (slide: QcSlide) => {
      if (!query.data || saving) return;
      setSaving(true);
      setError("");
      try {
        await qcReviewService.command(query.data, {
          slideId: slide.id,
          expectedDone: slide.isCompleted,
          desiredDone: !slide.isCompleted,
        });
        await invalidate();
      } catch (failure) {
        setError((failure as Error).message);
        await query.refetch();
      } finally {
        setSaving(false);
      }
    },
    [invalidate, query, saving]
  );
  const handleDone = useCallback(async () => {
    if (!query.data || saving) return;
    setSaving(true);
    setError("");
    try {
      await qcReviewService.command(query.data);
      notifications.show({
        color: "green",
        title: "QC review completed",
        message: "All QC slides are Done.",
        autoClose: 4000,
      });
      onClose();
      await invalidate();
    } catch (failure) {
      setError((failure as Error).message);
      await query.refetch();
    } finally {
      setSaving(false);
    }
  }, [invalidate, onClose, query, saving]);
  return (
    <Modal
      opened
      onClose={onClose}
      title={`QC Slide Review — ${query.data?.caseNumber || "Loading"}`}
      size="95%"
      closeButtonProps={{ "aria-label": "Close QC Slide Review" }}
    >
      <Stack>
        {(error || query.isError) && (
          <Alert color="red" title="QC review unavailable" role="alert">
            {error || "The current QC review could not be loaded."}
          </Alert>
        )}
        {query.isLoading && <Loader aria-label="Loading QC slides" />}
        {query.data?.failedQc && (
          <Alert color="red" title="Failed QC" role="status">
            A slide was marked incomplete and returned to cutting. Complete its
            rework or recheck it if it was marked incomplete by mistake.
          </Alert>
        )}
        {query.data && (
          <Table.ScrollContainer minWidth={900}>
            <Table withTableBorder withColumnBorders fz="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Block</Table.Th>
                  <Table.Th>Slide ID</Table.Th>
                  <Table.Th>Stain</Table.Th>
                  <Table.Th>Ordered By</Table.Th>
                  <Table.Th>Date Ordered</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Done</Table.Th>
                  <Table.Th>Remarks</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {query.data.slides.map((slide) => (
                  <QcSlideTableRow
                    key={slide.id}
                    slide={slide}
                    disabled={saving}
                    onChange={handleSlideChange}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {query.data && !query.data.canComplete && (
          <Text size="sm">
            Mark all related slides Done and resolve any outstanding lab work
            before completing QC review.
          </Text>
        )}
        <Group justify="space-between">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleDone}
            loading={saving}
            disabled={!query.data?.canComplete || query.isFetching || saving}
          >
            Done
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
